'use client';

import { Container, useModal } from '../..';
import Image from 'next/image';
import { useGlobalUI } from 'contexts/GlobalUIContext';
import GoogleAuthButtonProps from 'components/auth/GoogleAuthButton';
import {
  generateUltraworkMagicLink,
  PartnerElementData,
} from 'utils/affiliateHelpers';
import { useEffect, useRef, useState } from 'react';
import { useSession } from 'next-auth/react';
import { CheckCircleSF } from 'assets/icons';
import { useSearchParams } from 'next/navigation';
import { PrimaryButton } from 'components/shared/PrimaryButton';
import { getAssetUrl } from 'lib/config/assetUrls';
import styles from './Hero.module.css';

const MagicLinkLoginModal = ({
  className,
  companyCode,
  initialEmail,
  reason,
}: {
  className?: string;
  companyCode: string;
  // Pre-fills the form when re-prompting after a spent magic link or an
  // expired session. The member sends the new link themselves.
  initialEmail?: string;
  reason?: string;
}) => {
  const [error, setError] = useState<null | string>(null);
    const [emailSent, setEmailSent] = useState(false);
    const [loading, setLoading] = useState(false);

    const generateAndSendMagicLink = async (
      email: string,
      companyCode: string,
    ) => {
      setLoading(true);
      const response = await generateUltraworkMagicLink(email, companyCode);
      if (response.status == 'ok') {
        setEmailSent(true);
      } else {
        setError(response.message as string);
      }
      setLoading(false);
    };

    const FormView = (
      <>
        <h1>
          Sign in
          <br />
          <span>with Magic Link</span>
        </h1>

        {reason ? <p className={styles.error}>{reason}</p> : null}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            const emailInput = (e.target as HTMLFormElement).email.value;
            if (!emailInput) {
              setError('Email required');
              return;
            }
            generateAndSendMagicLink(emailInput, companyCode);
          }}
        >
          <label>
            <span>Email</span>
            <input
              name="email"
              type="email"
              required
              placeholder="steve@apple.com"
              defaultValue={initialEmail ?? ''}
            />
          </label>
          <PrimaryButton type="submit" className="button" disabled={loading}>
            {loading ? (
              <>
                <span className={styles.loader}>
                  <i style={{ animationDelay: '0' }}>•</i>
                  <i style={{ animationDelay: '0.05s' }}>•</i>
                  <i style={{ animationDelay: '0.1s' }}>•</i>
                </span>
              </>
            ) : (
              'Send me a Magic Link'
            )}
          </PrimaryButton>
        </form>
        {error ? <p className={styles.error}>{error}</p> : null}
      </>
    );

    const SuccessView = (
      <div className={styles.successContent}>
        <CheckCircleSF />
        <h2>Check your email for the magic link</h2>
        <p>
          If you don&apos;t see it in your inbox, try checking your spam or junk
          folder.
        </p>
      </div>
    );

  return (
    <div className={className ? `${styles.magicModal} ${className}` : styles.magicModal}>
      {emailSent ? SuccessView : FormView}
    </div>
  );
};

export const Hero = (
  props: PartnerElementData & {
    className?: string;
    setEmailValid: (i: boolean) => void;
    emailLinkAuthActive?: boolean;
    // Set (to the email that needs re-verifying, plus why) whenever the
    // caller detects a dead session -- a refreshed page whose magic-link
    // token already got spent, or a discount fetch that 401'd because the
    // 30-minute session expired. Opens the sign-in modal automatically
    // instead of leaving the page looking signed-out with no explanation.
    reverifyEmail?: string | null;
    reverifyReason?: string;
  },
) => {
    const GlobalUI = useGlobalUI();
    const rootRef = useRef<HTMLDivElement>(null);
    const searchParams = useSearchParams();

    const ModalClient = useModal();

    const session = useSession();

    useEffect(() => {
      if (!props.reverifyEmail) return;
      ModalClient.set(
        <MagicLinkLoginModal
          companyCode={props.code}
          initialEmail={props.reverifyEmail}
          reason={
            props.reverifyReason ??
            'Your sign-in expired. Send yourself a new link.'
          }
        />,
      );
      ModalClient.show();
      // ModalClient's setters are stable useCallbacks; keying on
      // reverifyEmail alone avoids reopening on every Hero re-render.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.reverifyEmail]);

    const WrongEmailModal = (
      <div style={{ padding: '42px 24px', maxWidth: '500px' }}>
        <h1
          style={{
            fontWeight: '500',
            fontSize: '2.4rem',
            marginBottom: '12px',
          }}
        >
          Wrong Email
        </h1>
        <p
          style={{
            fontWeight: '400',
            fontSize: '1.9rem',
            color: 'rgba(0,0,0,0.6)',
          }}
        >
          Sorry, you are not authorised to access this page. Please sign in with{' '}
          {props.name} account.
        </p>
      </div>
    );

    const sessionCallback = (s: any) => {
      if (s && s.user && s.user.email) {
        if (String(s.user.email).endsWith(props.email_template)) {
          props.setEmailValid(true);
          window.scrollTo({
            top: rootRef.current?.getBoundingClientRect().bottom ?? 0,
            behavior: 'smooth',
          });
        } else {
          ModalClient.set(WrongEmailModal);
          ModalClient.show();
        }
        return;
      }
      props.setEmailValid(false);
    };

    const handleEmailLogin = () => {
      if (props.emailLinkAuthActive) return;

      ModalClient.set(<MagicLinkLoginModal companyCode={props.code} />);
      ModalClient.show();
    };

    const EmailButton = (
      <button
        onClick={handleEmailLogin}
        className={`${styles.cta} ${props.emailLinkAuthActive ? styles.secondary : ''}`}
      >
        {props.emailLinkAuthActive
          ? `Signed in as ${decodeURIComponent(searchParams?.get('email') ?? '')}`
          : 'Sign in with Magic Link'}
      </button>
    );

    const GoogleButton = (
      <GoogleAuthButtonProps
        callback={(d) => sessionCallback(d)}
        className={`${styles.cta} ${styles.secondary} ${session.data ? styles.active : ''}`}
        template={props.email_template}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M17.64 9.20419C17.64 8.56601 17.5827 7.95237 17.4764 7.36328H9V10.8446H13.8436C13.635 11.9696 13.0009 12.9228 12.0477 13.561V15.8192H14.9564C16.6582 14.2524 17.64 11.9451 17.64 9.20419Z"
            fill="#4285F4"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8.99976 18C11.4298 18 13.467 17.1941 14.9561 15.8195L12.0475 13.5613C11.2416 14.1013 10.2107 14.4204 8.99976 14.4204C6.65567 14.4204 4.67158 12.8372 3.96385 10.71H0.957031V13.0418C2.43794 15.9831 5.48158 18 8.99976 18Z"
            fill="#34A853"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3.96409 10.7098C3.78409 10.1698 3.68182 9.59301 3.68182 8.99983C3.68182 8.40664 3.78409 7.82983 3.96409 7.28983V4.95801H0.957273C0.347727 6.17301 0 7.54755 0 8.99983C0 10.4521 0.347727 11.8266 0.957273 13.0416L3.96409 10.7098Z"
            fill="#FBBC05"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8.99976 3.57955C10.3211 3.57955 11.5075 4.03364 12.4402 4.92545L15.0216 2.34409C13.4629 0.891818 11.4257 0 8.99976 0C5.48158 0 2.43794 2.01682 0.957031 4.95818L3.96385 7.29C4.67158 5.16273 6.65567 3.57955 8.99976 3.57955Z"
            fill="#EA4335"
          />
        </svg>{' '}
        <span>Sign in with Google</span>
      </GoogleAuthButtonProps>
    );

    const getAuthBtns = () => {
      const op = [];

      if (!session.data) op.push(EmailButton);
      if (!props.emailLinkAuthActive) op.push(GoogleButton);

      return <>{op.map((e) => e)}</>;
    };

    return (
      <section
        className={
          props.className
            ? `${styles.root} ${props.className}`
            : styles.root
        }
        ref={rootRef}
      >
        <div
          className={styles.gradient}
          style={
            {
              top: GlobalUI.headerHeight ?? 0 + 'px',
              background: props.brandColor ?? '333',
            } as React.CSSProperties
          }
        />
        <div
          className={styles.gradientOverlay}
          style={
            {
              top: GlobalUI.headerHeight ?? 0 + 'px',
            } as React.CSSProperties
          }
        />
        <Container className={styles.content}>
          <div className={styles.orgCollabTag}>
            <span className={styles.logo}>
              <Image
                fill
                src={getAssetUrl('/web_v2/illustrations/uhLogo.svg')}
                alt="uh_logo"
              />{' '}
            </span>
            <span style={{ margin: '0 6px' }}>for</span>
            <span className={`${styles.logo} ${styles.partner}`}>
              {props.image ? (
                <Image fill src={props.image} alt={props.name} />
              ) : (
                // hardcoded for ultrahuman
                <b>{props.name !== 'Ultrahuman' ? props.name : 'its team'}</b>
              )}
              {/* TODO: style for text partner name */}
            </span>
          </div>

          <h1 className={styles.corporateHead}>
            {props.title ? props.title : 'You have been invited'}
          </h1>

          <p className={styles.corporateSubtitle}>
            {props.subtitle
              ? props.subtitle
              : 'Join UltraWork and be part of a network pushing the boundaries of health, performance, and human potential. Access cutting-edge technology and insights designed to optimize the way you live, work, and recover.'}
          </p>

          {/* <Link
            className="id-text"
            href={{
              pathname: 'ring/buy',
              query: {
                ...router.query,
                affiliateCode: code,
              },
            }}
          >
            {cta_title}
          </Link> */}

          <div className={styles.ctaContainer}>{getAuthBtns()}</div>

          {/* <Disclaimer>
            <p>
              <span>Disclaimer: </span>We currently support Google Workspaces
              and are actively working to expand options. Signing in with your
              work email is for verification only—it does not grant us access to
              your work or organization.
            </p>
          </Disclaimer> */}
        </Container>

        {/* <div className="noise-bg" aria-hidden="true"></div> */}
      </section>
    );
};
